import { Link } from '@tanstack/react-router'
import { parseAsStringLiteral, useQueryStates } from 'nuqs'
import { Search } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { StatusPill } from '@/components/status-pill'
import { useUrlSearchText } from '@/hooks/use-url-search-text'
import {
  ACCOUNT_ORDER,
  ACCOUNT_SHORT_LABELS,
  ACCOUNT_TONES,
  SUPPLIER_KIND_TONES,
  SUPPLIER_ROLE_LABELS,
} from '@/lib/suppliers'
import {
  SUPPLIER_KIND_LABELS,
  supplierCoverage,
  useSuppliers,
  type SupplierListItem,
} from '@/api/sourcing'
import { SearchInput } from '@/components/search-input'
import { ListFilter, ListFilters } from '@/components/list-filters'
import { resultCount } from '@/lib/utils'

const KIND_FILTERS = [
  { value: 'all', label: 'All kinds' },
  { value: 'rep_group', label: 'Rep groups' },
  { value: 'distributor', label: 'Distributors' },
  { value: 'online_retailer', label: 'Online retailers' },
  { value: 'buying_group', label: 'Buying groups' },
] as const

type KindFilter = (typeof KIND_FILTERS)[number]['value']

// The supplier's people that matter at a glance: who gets orders and spec credit requests.
const GLANCE_ROLES = ['po_primary', 'spec_credit'] as const

function place(supplier: SupplierListItem) {
  return [supplier.email_domain, [supplier.address?.city, supplier.address?.state].filter(Boolean).join(', ')]
    .filter(Boolean)
    .join(' · ')
}

function brands(supplier: SupplierListItem) {
  const names = supplier.top_manufacturers ?? []
  if (!names.length) return null
  const more = (supplier.preferred_for ?? 0) - names.length
  return names.join(', ') + (more > 0 ? ` +${more} more` : '')
}

function SupplierName({ supplier }: { supplier: SupplierListItem }) {
  return (
    <div className="space-y-0.5">
      <div className="flex flex-wrap items-center gap-2">
        <Link
          to="/suppliers/$supplierId"
          params={{ supplierId: supplier.uuid }}
          className="font-medium text-primary hover:underline"
        >
          {supplier.name}
        </Link>
        <StatusPill tone={SUPPLIER_KIND_TONES[supplier.kind]}>{SUPPLIER_KIND_LABELS[supplier.kind]}</StatusPill>
      </div>
      {place(supplier) && <p className="text-xs text-muted-foreground">{place(supplier)}</p>}
    </div>
  )
}

function Manufacturers({ supplier }: { supplier: SupplierListItem }) {
  return (
    <div className="space-y-0.5">
      <p className="text-sm font-medium tabular-nums">{supplierCoverage(supplier)}</p>
      {brands(supplier) && <p className="text-xs text-muted-foreground">{brands(supplier)}</p>}
    </div>
  )
}

function Contacts({ supplier }: { supplier: SupplierListItem }) {
  if (supplier.kind === 'buying_group') return <span className="text-xs text-muted-foreground">-</span>
  const rows = GLANCE_ROLES.map((role) => ({
    role,
    email: supplier.contacts?.find((person) => person.role === role)?.contact.email,
  }))
  if (!rows.some((row) => row.email)) return <StatusPill tone="warning">No contacts set</StatusPill>
  return (
    <div className="space-y-0.5 text-xs">
      {rows.map((row) => (
        <p key={row.role} className="truncate">
          <span className="text-muted-foreground">{SUPPLIER_ROLE_LABELS[row.role]} </span>
          {row.email ?? <span className="text-amber-700 dark:text-amber-300">not set</span>}
        </p>
      ))}
    </div>
  )
}

function Accounts({ supplier }: { supplier: SupplierListItem }) {
  const tally = supplier.accounts ?? {}
  const shown = ACCOUNT_ORDER.filter((status) => tally[status])
  if (!shown.length) return <span className="text-xs text-muted-foreground">-</span>
  return (
    <div className="flex flex-wrap gap-1">
      {shown.map((status) => (
        <StatusPill key={status} tone={ACCOUNT_TONES[status]}>
          {tally[status]} {ACCOUNT_SHORT_LABELS[status]}
        </StatusPill>
      ))}
    </div>
  )
}

export function SuppliersPage() {
  const [search, setSearch] = useUrlSearchText()
  const [{ kind }, setFilters] = useQueryStates({
    kind: parseAsStringLiteral(KIND_FILTERS.map((option) => option.value)).withDefault('all'),
  })
  const { data, isLoading } = useSuppliers()
  const query = search.trim().toLowerCase()
  const suppliers = (data ?? []).filter(
    (supplier) =>
      (kind === 'all' || supplier.kind === kind) &&
      (!query ||
        supplier.name.toLowerCase().includes(query) ||
        (supplier.email_domain ?? '').includes(query) ||
        (supplier.top_manufacturers ?? []).some((name) => name.toLowerCase().includes(query))),
  )

  return (
    <div className="mx-auto max-w-7xl space-y-4">
      <h1 className="text-2xl font-bold tracking-tight">Suppliers</h1>

      <ListFilters
        count={data ? resultCount(suppliers.length, 'supplier') : undefined}
        active={kind === 'all' ? 0 : 1}
        onClear={() => void setFilters({ kind: null })}
        search={
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <SearchInput
              placeholder="Search by name or email domain..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="pl-9"
            />
          </div>
        }
      >
        <ListFilter label="Kind">
          <Select value={kind} onValueChange={(value) => void setFilters({ kind: value as KindFilter })}>
            <SelectTrigger aria-label="Supplier kind" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {KIND_FILTERS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ListFilter>
      </ListFilters>

      {isLoading ? (
        <div role="status" aria-label="Loading suppliers" className="space-y-2">
          {Array.from({ length: 8 }).map((_, index) => (
            <Skeleton key={index} className="h-16 w-full" />
          ))}
        </div>
      ) : !suppliers.length ? (
        <div className="py-12 text-center text-muted-foreground">No suppliers found</div>
      ) : (
        <>
          <div className="hidden lg:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Manufacturers</TableHead>
                  <TableHead>Contacts</TableHead>
                  <TableHead>Accounts</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {suppliers.map((supplier) => (
                  <TableRow key={supplier.id} className="align-top">
                    <TableCell className="max-w-[280px] whitespace-normal">
                      <SupplierName supplier={supplier} />
                    </TableCell>
                    <TableCell className="max-w-[340px] whitespace-normal">
                      <Manufacturers supplier={supplier} />
                    </TableCell>
                    <TableCell className="max-w-[280px] whitespace-normal">
                      <Contacts supplier={supplier} />
                    </TableCell>
                    <TableCell className="max-w-[300px] whitespace-normal">
                      <Accounts supplier={supplier} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="space-y-2 lg:hidden">
            {suppliers.map((supplier) => (
              <div key={supplier.id} className="space-y-2 rounded-lg border bg-card p-3">
                <SupplierName supplier={supplier} />
                <Manufacturers supplier={supplier} />
                <Contacts supplier={supplier} />
                <Accounts supplier={supplier} />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
